import { selectProfile } from '../../../../store/workspace/selectors';
import { useDispatch, useSelector } from '../../../../store/store';
import { useCallback } from 'react';
import {
    setPlansModalShow,
    setInviteMemberModal,
    setAddExtraMemberModal,
    setAddExtraJobPostModal,
    setAddExtraApplicantModal,
} from '../../../../store/workspace/slice';

/**
 * Every "Add More" entry point on the account page is gated the same way: a
 * free trial cannot buy extras, so it is pushed to the plans modal instead.
 */
export function useExtraServiceGate() {
    const dispatch = useDispatch();
    const profile = useSelector(selectProfile);
    const isFreeTrial = profile?.planType === 'free_trial';

    const openPurchase = useCallback((openModal: () => void) => {
        if (!profile?.planType) return;

        if (isFreeTrial) {
            dispatch(setPlansModalShow(true));
            return;
        }

        openModal();
    }, [
        dispatch,
        isFreeTrial,
        profile?.planType,
    ]);

    const addMoreMembers = useCallback(
        () => openPurchase(() => dispatch(setAddExtraMemberModal(true))),
        [dispatch, openPurchase],
    );

    const addMoreJobPosts = useCallback(
        () => openPurchase(() => dispatch(setAddExtraJobPostModal(true))),
        [dispatch, openPurchase],
    );

    const addMoreApplicants = useCallback(
        () => openPurchase(() => dispatch(setAddExtraApplicantModal(true))),
        [dispatch, openPurchase],
    );

    /**
     * Inviting reuses a free seat when there is one and falls back to buying an
     * extra seat once every seat of the plan is taken.
     */
    const inviteMember = useCallback(() => {
        if (!profile?.invitations || !profile.memberCounter) return;

        if (isFreeTrial) {
            dispatch(setPlansModalShow(true));
            return;
        }

        if (profile.invitations.length < profile.memberCounter) {
            dispatch(setInviteMemberModal(true));
            return;
        }

        if (profile.invitations.length === profile.memberCounter) {
            dispatch(setAddExtraMemberModal(true));
        }
    }, [
        dispatch,
        isFreeTrial,
        profile?.invitations,
        profile?.memberCounter,
    ]);

    return {
        inviteMember,
        addMoreMembers,
        addMoreJobPosts,
        addMoreApplicants,
    };
};
